import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock, Check } from "lucide-react";
import { useLocation } from "wouter";

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  reason: "FREE_TIER_INVITED_ONLY" | "BROKER_UPGRADE_REQUIRED" | "FREE_TIER_LIMIT_REACHED" | "SEARCH_AND_BID_REQUIRED" | "CARRIER_UPGRADE_REQUIRED";
  userType?: string;
}

const UPGRADE_CONTENT = {
  FREE_TIER_INVITED_ONLY: {
    title: "Upgrade to bid on this load",
    description: "You can already bid for free on loads from partners who've invited you. To bid on loads you discover yourself on the public board, upgrade to a Search & Bid plan.",
    benefits: [
      "Bid on any load on the board",
      "See full company details before bidding",
      "Post your own truck space",
    ],
    price: "$25/month",
    cta: "Upgrade — $25/month",
  },
  SEARCH_AND_BID_REQUIRED: {
    title: "Upgrade to bid on this load",
    description: "You can already bid for free on loads from partners who've invited you. To bid on loads you discover yourself on the public board, upgrade to a paid plan.",
    benefits: [
      "Bid on any load on the board",
      "See full company details before bidding",
      "Post your own truck space",
    ],
    price: "$25/month",
    cta: "Upgrade — $25/month",
  },
  BROKER_UPGRADE_REQUIRED: {
    title: "Upgrade to post loads",
    description: "Posting loads requires a Search, Bid & Post  membership. Upgrade to get the complete broker experience on Fill The Trucks.",
    benefits: [
      "Post loads",
      "Bid on any load on the board",
      "Full marketplace access",
    ],
    price: "$45/month",
    cta: "Upgrade — $45/month",
  },
  FREE_TIER_LIMIT_REACHED: {
    title: "You've reached your free posting limit",
    description: "Your free plan includes 3 shipment posts per month. Upgrade for unlimited posting.",
    benefits: [
      "Unlimited shipment posts",
      "Notify all your partners on every load",
    ],
    price: "$25/month",
    cta: "Upgrade — $25/month",
  },
  CARRIER_UPGRADE_REQUIRED: {
    title: "Upgrade to post truck space",
    description: "Posting your truck space requires a Search & Bid plan. Upgrade to post your availability and bid on any load on the public board.",
    benefits: [
      "Post your truck space for shippers & brokers to find",
      "Bid on any load on the public board",
      "See full company details on all loads",
    ],
    price: "$25/month",
    cta: "Upgrade to Search & Bid",
  },
};

export function UpgradeModal({ open, onClose, reason, userType }: UpgradeModalProps) {
  const [, setLocation] = useLocation();
  const content = UPGRADE_CONTENT[reason];

  function handleUpgradeClick() {
    onClose();
    setLocation("/upgrade");
  }

  function handleMaybeLater() {
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleMaybeLater(); }}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-upgrade-modal">
        <DialogHeader>
          <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-3">
            <Lock className="w-6 h-6 text-blue-600" />
          </div>
          <DialogTitle className="text-center">{content.title}</DialogTitle>
          <DialogDescription className="text-center">
            {content.description}
          </DialogDescription>
        </DialogHeader>

        <div className="bg-muted/50 rounded-lg p-4 my-2">
          {content.benefits.map((benefit, i) => (
            <div key={i} className="flex items-center gap-2 mb-2 last:mb-0">
              <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span className="text-sm">{benefit}</span>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          <Button
            className="w-full"
            onClick={handleUpgradeClick}
            data-testid="button-upgrade-confirm"
          >
            {content.cta}
          </Button>
          <Button
            variant="ghost"
            className="w-full"
            onClick={handleMaybeLater}
            data-testid="button-upgrade-maybe-later"
          >
            Maybe later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
